// services/brand.service.ts
import axios from "axios";
import { Brand, BrandsData, CreateBrandPayload, UpdateBrandPayload } from "../../models/brands.inventory.model";
import { ApiResponse } from "../../models/api.response.model";
import { authToken } from "../../scripts/authentication.script";

export class BrandDA {
  private static apiResponse: ApiResponse = {
    status: 200,
    message: "Success",
    data: null,
  };

  // GET all brands with pagination
  public static async getBrands(id_company: string, page: number = 1, limit: number = 10): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let allItems: Brand[] = [];
      let error: Error | null = null;

      await axios
        .get("../../../../../database/brands.inventory.data.json")
        .then((response) => {
          allItems = response.data.data.items;
        })
        .catch((error) => {
          alert(error);
        });

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      if (allItems) {
        const total = allItems.length;
        const pages = limit > 0 ? Math.ceil(total / limit) : 1;
        const safePage = Math.min(Math.max(page, 1), pages || 1);
        const start = (safePage - 1) * limit;
        const items = allItems.slice(start, start + limit);

        this.apiResponse = {
          status: 200,
          message: "Success",
          data: {
            items,
            pagination: { page: safePage, limit, total, pages },
          } as BrandsData,
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/brands?page=${page}&limit=${limit}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .get(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.success ? response.data.data : null,
          };
        })
        .catch((error) => {
          this.apiResponse = {
            status: error.response?.status || 500,
            message: error.response?.statusText || "Error",
            data: error.response?.data?.error ??
              error.response?.data ?? {
                code: "NETWORK_ERROR",
                message: error.message,
              },
          };
        });
    }

    return this.apiResponse;
  }

  // GET brand by ID
  public static async getBrand(id_company: string, id_brand: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let brand: Brand | null = null;
      let error: Error | null = null;

      await axios
        .get("../../../../../database/brands.inventory.data.json")
        .then((response) => {
          const items = response.data.data.items;
          brand = items.find((item: Brand) => item.id === id_brand) || null;
        })
        .catch((error) => {
          alert(error);
        });

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      if (brand) {
        this.apiResponse = {
          status: 200,
          message: "Success",
          data: brand,
        };
      } else {
        this.apiResponse = {
          status: 404,
          message: "Brand not found",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/brands/${id_brand}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .get(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.success ? response.data.data : null,
          };
        })
        .catch((error) => {
          this.apiResponse = {
            status: error.response?.status || 500,
            message: error.response?.statusText || "Error",
            data: error.response?.data?.error ??
              error.response?.data ?? {
                code: "NETWORK_ERROR",
                message: error.message,
              },
          };
        });
    }

    return this.apiResponse;
  }

  // POST create new brand
  public static async postBrand(id_company: string, payload: CreateBrandPayload): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      
      // Simulate successful creation
      const newBrand: Brand = {
        id: `br_${Math.floor(Math.random() * 1000).toString()}`,
        name: payload.name,
        logo: "",
        company_id: id_company,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      this.apiResponse = {
        status: 201,
        message: "Success",
        data: newBrand,
      };
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/brands`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: FormData = new FormData();
      data.append("name", payload.name);
      if (payload.logo) {
        data.append("logo", payload.logo);
      }

      await axios
        .post(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.success ? response.data.data : null,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ??
              err.response?.data ?? {
                code: "NETWORK_ERROR",
                message: err.message,
              },
          };
        });
    }

    return this.apiResponse;
  }

  // PUT update brand
  public static async putBrand(id_company: string, id_brand: string, payload: UpdateBrandPayload): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let brandsResponse: BrandsData = {
        items: [],
        pagination: { page: 1, limit: 10, total: 0, pages: 0 },
      };
      let error: Error | null = null;

      await axios
        .get("../../../../../database/brands.inventory.data.json")
        .then((response) => {
          brandsResponse = response.data.data;
        })
        .catch((err) => {
          alert(err);
        });

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((err) => {
          alert(err);
        });

      const itemFound = brandsResponse.items.find((item) => item.id === id_brand);

      if (itemFound) {
        if (payload.name !== undefined) itemFound.name = payload.name;
        if (payload.logo !== undefined) itemFound.logo = payload.logo;
        if (payload.is_active !== undefined) itemFound.is_active = payload.is_active;
        itemFound.updated_at = new Date().toISOString();

        this.apiResponse = {
          status: 200,
          message: "Success",
          data: itemFound,
        };
      } else {
        this.apiResponse = {
          status: 404,
          message: "Brand not found",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/brands/${id_brand}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: FormData = new FormData();
      if (payload.name !== undefined) data.append("name", payload.name);
      if (payload.logo) data.append("logo", payload.logo);
      if (payload.is_active !== undefined) data.append("is_active", String(payload.is_active));

      await axios
        .put(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.success ? response.data.data : null,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ??
              err.response?.data ?? {
                code: "NETWORK_ERROR",
                message: err.message,
              },
          };
        });
    }

    return this.apiResponse;
  }

  // DELETE brand
  public static async deleteBrands(
    id_company: string,
    id_brand: string, // Ahora recibe un array de IDs
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let brandsResponse: BrandsData = {
        items: [],
        pagination: { page: 1, limit: 10, total: 0, pages: 0 },
      };
      let error: Error | null = null;

      await axios
        .get("../../../../../database/brands.inventory.data.json")
        .then((response) => {
          brandsResponse = response.data.data;
        })
        .catch((err) => {
          alert(err);
        });

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((err) => {
          alert(err);
        });

      // Filtrar los elementos que coincidan con algún ID del array
      const itemsToDelete = brandsResponse.items.find((item) => id_brand === item.id);

      if (itemsToDelete) {
        // Eliminar los elementos encontrados
        brandsResponse.items = brandsResponse.items.filter((item) => item.id !== id_brand);

        this.apiResponse = {
          status: 200,
          message: "Success",
          data: {
            message: "Successfully brand deleted",
          },
        };
      } else {
        this.apiResponse = {
          status: 404,
          message: "No brands found to delete",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/brands/${id_brand}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .delete(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.success ? response.data.data : null,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ??
              err.response?.data ?? {
                code: "NETWORK_ERROR",
                message: err.message,
              },
          };
        });
    }

    return this.apiResponse;
  }
}
